import React from 'react';
import styles from './table-typography.module.css';

import { cn } from '../lib/utils';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Concise accessible purpose when a visible caption is not appropriate. */
  accessibleName?: string;
}

/** Canonical semantic table root for intentionally authored native tables. */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, accessibleName, ...props }, ref) => (
    <table
      ref={ref}
      className={cn(
        styles.table,
        'w-full border-collapse text-left font-body text-body tabular-nums text-text-body',
        '[&_thead_tr]:bg-surface-soft [&_th]:h-11 [&_th]:px-4 [&_th]:py-2 [&_th]:text-body-sm [&_th]:leading-snug [&_th]:font-semibold [&_th]:normal-case [&_th]:tracking-normal [&_th]:text-text-muted [&_th]:font-body [&_th]:whitespace-normal [&_th]:break-words [&_th]:[overflow-wrap:anywhere]',
        // Low specificity preserves consumer rowClassName background overrides.
        '[&_tbody_tr]:border-b [&_tbody_tr]:border-border [&_tbody_tr]:last:border-0 [:where(&_tbody_tr)]:bg-surface-sheet [:where(&_tbody_tr:hover)]:bg-surface-soft [&_tbody_tr[aria-selected=true]]:bg-selected [&_tbody_tr[aria-selected=true]:hover]:bg-selected',
        '[&_th]:overflow-hidden [&_td]:h-11 [&_td]:px-4 [&_td]:py-2 [&_td]:overflow-hidden [&_td_button]:min-h-11 [&_td_a]:min-h-11 sm:[&_td_button]:min-h-8 sm:[&_td_a]:min-h-8 [&_td]:break-words [&_td]:[overflow-wrap:anywhere] [:where(&_td:not([colspan]))]:min-w-32 [:where(&_th:not([colspan]))]:min-w-32 print:[&_td:not([colspan])]:min-w-0 [&_th:has(input[type=checkbox])]:min-w-0 [&_td:has(input[type=checkbox])]:min-w-0',
        '[&_tr>:first-child:not([colspan])]:sticky [&_tr>:first-child:not([colspan])]:left-0 [&_tr>:first-child:not([colspan])]:z-10 [&_tr>:first-child:not([colspan])]:bg-inherit [&_thead_tr>:first-child:not([colspan])]:z-30 print:[&_tr>:first-child:not([colspan])]:static print:[&_th]:static print:[&_td]:static',
        className,
      )}
      aria-label={accessibleName}
      data-table-root="true"
      {...props}
    />
  ),
);

Table.displayName = 'Table';
